// Company knowledge base used by the local assistant provider. Content is
// grounded in the site's own copy (About / Home) so answers stay accurate.
// When the AWS Lex + Bedrock Knowledge Base is wired up, this same content can
// seed the Bedrock KB (see scripts/aws/knowledge-base.md).

export interface KnowledgeEntry {
  /** Trigger keywords/phrases for matching a question. */
  keywords: string[]
  /** The answer, written to be read aloud comfortably. */
  answer: string
  /** Optional related page path to offer as a follow-up. */
  path?: string
}

export const COMPANY_NAME = 'BigData Ghana'

export const KNOWLEDGE: KnowledgeEntry[] = [
  {
    keywords: ['who are you', 'what is bigdata', 'what is big data', 'about the company', 'what do you do', 'who is bigdata'],
    answer:
      'BigData Ghana is a Ghanaian technology and intelligence company. We combine geospatial intelligence, cloud technologies, data analytics and AI to help organisations understand the places, systems, assets and risks behind their most important decisions.',
    path: '/about',
  },
  {
    keywords: ['founded', 'started', 'how old', 'history', 'years', 'since when', 'established'],
    answer:
      'BigData Ghana was founded in 2016. We have eight years of Ghana-specific spatial data and technical delivery. Along the way we became an AWS Partner in 2020 and launched the National Forest Monitoring System with the Forestry Commission in 2022.',
    path: '/about',
  },
  {
    keywords: ['services', 'offer', 'offerings', 'what can you do', 'capabilities'],
    answer:
      'Our core services are geospatial intelligence and earth observation, data analytics and business intelligence, AI and automation, and cloud platforms. Would you like me to open the services page?',
    path: '/services',
  },
  {
    keywords: ['geospatial', 'gis', 'mapping', 'earth observation', 'satellite', 'remote sensing'],
    answer:
      'Our geospatial intelligence turns maps, satellite imagery and spatial data into decisions grounded in Ghana. Global platforms can show you a map; we help you understand what that map means locally.',
    path: '/geospatial',
  },
  {
    keywords: ['cloud', 'aws', 'partner', 'infrastructure'],
    answer:
      'We are an AWS Partner with certified cloud engineers. We design and run cloud infrastructure so your data platforms are secure, scalable and reliable.',
    path: '/cloud-platforms',
  },
  {
    keywords: ['ai', 'automation', 'artificial intelligence', 'machine learning', 'bigconnect'],
    answer:
      'We build AI and automation solutions, including BigConnect AI, our AI-powered communication platform launched in 2024. We apply intelligence to decisions that shape how organisations grow, invest and operate.',
    path: '/ai-automation',
  },
  {
    keywords: ['analytics', 'business intelligence', 'dashboards', 'data analysis'],
    answer:
      'Our data analytics and business intelligence work turns raw data into clear dashboards and actionable insight, so teams make decisions backed by evidence.',
    path: '/data-analytics',
  },
  {
    keywords: ['clients', 'customers', 'who do you work with', 'sectors', 'industries'],
    answer:
      'We serve government, banking, agriculture, real estate and logistics across Ghana and West Africa. Clients include AFC, Indomie, the Parliament of Ghana, USAID, GIZ, Digital Earth Africa, NITA and the University of Ghana.',
    path: '/proof',
  },
  {
    keywords: ['projects', 'work', 'proof', 'case studies', 'track record', 'portfolio'],
    answer:
      'Our proof page shows real projects, from the National Forest Monitoring System to spatial platforms for government and risk intelligence for banking. Want me to open it?',
    path: '/proof',
  },
  {
    keywords: ['team', 'people', 'staff', 'how many', 'employees'],
    answer:
      'We are a focused, senior team of 15 or more, embedded directly into your project. Intelligence built by people who understand Ghana. I can take you to the team page.',
    path: '/team',
  },
  {
    keywords: ['careers', 'jobs', 'hiring', 'work with us', 'vacancies', 'join', 'apply'],
    answer:
      'We are always looking for people who like hard work, smart solutions and ambitious deadlines. Check our careers page for open roles.',
    path: '/careers',
  },
  {
    keywords: ['contact', 'reach', 'email', 'phone', 'get in touch', 'talk to someone'],
    answer:
      'You can reach our team through the contact page. Would you like me to open it for you?',
    path: '/contact',
  },
  {
    keywords: ['mission', 'vision', 'values', 'advantage', 'why you', 'why bigdata'],
    answer:
      'Our advantage is simple: global platforms can show you a map, but BigData Ghana helps you understand what it means in Ghana. We are intelligence-first, grounded in local expertise, driven by innovation and measured by real impact.',
    path: '/about',
  },
  {
    keywords: ['where', 'location', 'based', 'ghana', 'office'],
    answer:
      'BigData Ghana is based in Ghana and serves clients across Ghana and West Africa.',
    path: '/contact',
  },
  {
    keywords: ['bank', 'banking', 'financial', 'finance', 'lending', 'credit', 'insurance'],
    answer:
      'For banking and financial services, BigData Ghana helps with risk intelligence, location and collateral verification, and data analytics — turning spatial and operational data into clearer lending, investment and risk decisions. Data Analytics and Geospatial Intelligence are the most relevant services.',
    path: '/data-analytics',
  },
  {
    keywords: ['agriculture', 'farm', 'farming', 'crop', 'maize', 'yield', 'agri'],
    answer:
      'For agriculture, BigData Ghana combines earth observation and AI — for example Maize Intelligence for yield estimation and crop monitoring, and the National Forest Monitoring System. Geospatial Intelligence and AI and automation are the core services here.',
    path: '/geospatial',
  },
  {
    keywords: ['bigconnect', 'big connect', 'call', 'communication', 'customer service', 'voice agent'],
    answer:
      'BigConnect AI is our AI-powered communication platform, launched in 2024. It answers calls, understands intent and generates follow-up summaries, delivering enterprise-level, bilingual customer service for banking, healthcare, government and SMEs.',
    path: '/solutions',
  },
  {
    keywords: ['maize intelligence', 'crop monitoring', 'yield estimation'],
    answer:
      'Maize Intelligence is our solution for estimating maize yield and monitoring crop health using satellite data and AI, helping agriculture and food-security decisions.',
    path: '/solutions',
  },
  {
    keywords: ['sendline', 'sms', 'messaging', 'bulk sms'],
    answer:
      'SendlineSMS is our messaging solution for reaching customers and communities at scale across Ghana.',
    path: '/solutions',
  },
  {
    keywords: ['risk', 'risk intelligence', 'due diligence', 'fraud'],
    answer:
      'Our risk intelligence work brings together spatial data, analytics and AI so organisations — especially in banking and insurance — can assess assets, locations and exposure before they commit.',
    path: '/data-analytics',
  },
  {
    keywords: ['forest', 'forestry', 'monitoring', 'deforestation', 'forest trace'],
    answer:
      'We built the National Forest Monitoring System with the Forestry Commission in 2022, and Forest Trace AI for tracking forest change. This blends earth observation with AI for environmental monitoring.',
    path: '/proof',
  },
  {
    keywords: ['government', 'public sector', 'election', 'mapping', 'census', 'district'],
    answer:
      'For government, we deliver mapping, monitoring and data platforms — from constituency and district mapping to project monitoring and evaluation systems used across Ghana.',
    path: '/proof',
  },
]

const GREETINGS = ['hi', 'hello', 'hey', 'good morning', 'good afternoon', 'good evening', 'yo', 'howdy']

export function isGreeting(text: string): boolean {
  const q = text.toLowerCase().trim()
  return GREETINGS.some((g) => q === g || q.startsWith(g + ' ') || q.startsWith(g + '!'))
}

/** Simple keyword-overlap scorer for the local KB. */
export function matchKnowledge(text: string): KnowledgeEntry | null {
  const q = text.toLowerCase()
  let best: { entry: KnowledgeEntry; score: number } | null = null

  for (const entry of KNOWLEDGE) {
    let score = 0
    for (const kw of entry.keywords) {
      if (q.includes(kw)) score = Math.max(score, kw.length)
    }
    if (score > 0 && (!best || score > best.score)) best = { entry, score }
  }

  return best?.entry ?? null
}
